import React, { useEffect, useState } from 'react'
import { useFeatureStore } from '@store'
import { Plus, Eye, Edit2, Archive, Trash2, RotateCcw, Star, Filter, ArrowUpDown } from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader } from '@components/common'
import FeatureForm from './FeatureForm'
import FeatureView from './FeatureView'
import './Features.css'

/**
 * Features Management Page (Super Admin Only)
 *
 * Complete feature management interface with:
 * - List view with DataTable
 * - Search and filtering
 * - Create/Edit/View/Delete operations
 * - Statistics dashboard
 * - Pagination
 * - Responsive design
 */
const Features = () => {
	const {
		features,
		selectedFeature,
		loading,
		filters,
		pagination,
		stats,
		categories,
		fetchFeatures,
		fetchCategories,
		selectFeature,
		archiveFeature,
		deleteFeature,
		restoreFeature,
		setSearch,
		setStatusFilter,
		setCategoryFilter,
		setPage,
		nextPage,
		prevPage,
	} = useFeatureStore()

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [actionType, setActionType] = useState(null) // 'archive', 'delete', 'restore'
	const [selectedFeatureForAction, setSelectedFeatureForAction] = useState(null)

	// Fetch features and categories on mount
	useEffect(() => {
		const loadData = async () => {
			try {
				await Promise.all([fetchFeatures(), fetchCategories()])
			} catch (error) {
				console.error('Failed to load features data:', error)
			}
		}
		loadData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/**
	 * Handle create feature
	 */
	const handleCreate = () => {
		selectFeature(null)
		setShowCreateModal(true)
	}

	/**
	 * Handle view feature
	 */
	const handleView = (feature) => {
		selectFeature(feature)
		setShowViewModal(true)
	}

	/**
	 * Handle edit feature
	 */
	const handleEdit = (feature) => {
		selectFeature(feature)
		setShowEditModal(true)
	}

	/**
	 * Handle edit from view modal
	 */
	const handleEditFromView = () => {
		setShowViewModal(false)
		setShowEditModal(true)
	}

	/**
	 * Handle archive feature
	 */
	const handleArchive = (feature) => {
		setSelectedFeatureForAction(feature)
		setActionType('archive')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle delete feature
	 */
	const handleDelete = (feature) => {
		setSelectedFeatureForAction(feature)
		setActionType('delete')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle restore feature
	 */
	const handleRestore = (feature) => {
		setSelectedFeatureForAction(feature)
		setActionType('restore')
		setShowConfirmDialog(true)
	}

	/**
	 * Confirm action (archive/delete/restore)
	 */
	const confirmAction = async () => {
		if (!selectedFeatureForAction) return

		try {
			if (actionType === 'archive') {
				await archiveFeature(selectedFeatureForAction.id)
			} else if (actionType === 'delete') {
				await deleteFeature(selectedFeatureForAction.id)
			} else if (actionType === 'restore') {
				await restoreFeature(selectedFeatureForAction.id)
			}

			setShowConfirmDialog(false)
			setSelectedFeatureForAction(null)
			setActionType(null)
		} catch (error) {
			console.error('Action failed:', error)
		}
	}

	/**
	 * Cancel action
	 */
	const cancelAction = () => {
		setShowConfirmDialog(false)
		setSelectedFeatureForAction(null)
		setActionType(null)
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = (status, deletedAt) => {
		if (deletedAt || status === 'archived') {
			return <span className="badge badge--archived">Archived</span>
		}
		return <span className="badge badge--active">Active</span>
	}

	/**
	 * Get action confirmation details
	 */
	const getConfirmDetails = () => {
		if (!selectedFeatureForAction || !actionType) return {}

		const featureName = selectedFeatureForAction.name

		const details = {
			archive: {
				title: 'Archive Feature?',
				message: `Are you sure you want to archive "${featureName}"? This will hide it from package selection, but it will remain in existing packages.`,
				confirmText: 'Archive',
				variant: 'warning',
			},
			delete: {
				title: 'Delete Feature?',
				message: `Are you sure you want to delete "${featureName}"? This action will permanently remove it from the system.`,
				confirmText: 'Delete',
				variant: 'danger',
			},
			restore: {
				title: 'Restore Feature?',
				message: `Are you sure you want to restore "${featureName}"? This will make it available for package selection again.`,
				confirmText: 'Restore',
				variant: 'info',
			},
		}

		return details[actionType] || {}
	}

	// Define table columns
	const columns = [
		{
			key: 'name',
			label: 'Feature',
			sortable: true,
			render: (feature) => (
				<div className="feature-cell">
					<div className="feature-cell__name">{feature.name}</div>
					{feature.category && (
						<div className="feature-cell__category">
							<span className="badge badge--secondary">{feature.category}</span>
						</div>
					)}
				</div>
			),
		},
		{
			key: 'description',
			label: 'Description',
			render: (feature) => (
				<div className="description-cell">
					{feature.description || '-'}
				</div>
			),
		},
		{
			key: 'display_order',
			label: 'Order',
			sortable: true,
			align: 'center',
			render: (feature) => (
				<div className="order-cell">
					{feature.display_order || 0}
				</div>
			),
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			render: (feature) => getStatusBadge(feature.status, feature.deleted_at),
		},
	]

	// Define table actions
	const actions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: handleEdit,
			show: (feature) => !feature.deleted_at && feature.status !== 'archived',
		},
		{
			label: 'Archive',
			icon: <Archive size={16} />,
			onClick: handleArchive,
			variant: 'warning',
			show: (feature) => !feature.deleted_at && feature.status !== 'archived',
		},
		{
			label: 'Delete',
			icon: <Trash2 size={16} />,
			onClick: handleDelete,
			variant: 'danger',
			show: (feature) => !feature.deleted_at && feature.status !== 'archived',
		},
		{
			label: 'Restore',
			icon: <RotateCcw size={16} />,
			onClick: handleRestore,
			variant: 'primary',
			show: (feature) => feature.deleted_at || feature.status === 'archived',
		},
	]

	const confirmDetails = getConfirmDetails()

	return (
		<div className="features-page">
			{/* Page Header with Breadcrumbs */}
			<PageHeader
				title="Features"
				description="Manage package features and capabilities"
				breadcrumbs={[{ label: 'Features' }]}
				actions={
					<Button variant="primary" onClick={handleCreate}>
						<Plus size={20} />
						<span>Add Feature</span>
					</Button>
				}
			/>

			{/* Statistics */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--primary">
						<Star size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Total Features</div>
						<div className="stat-card__value">{stats.total}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--success">
						<Star size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Active</div>
						<div className="stat-card__value">{stats.active}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--info">
						<Archive size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Archived</div>
						<div className="stat-card__value">{stats.archived}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--warning">
						<Star size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">In Use</div>
						<div className="stat-card__value">{stats.inUse}</div>
					</div>
				</div>
			</div>

			{/* Filters & Search */}
			<div className="filters-section">
				<SearchBar
					value={filters.search}
					onChange={setSearch}
					placeholder="Search features..."
					loading={loading}
				/>

				<div className="filters-section__divider">
					<Filter size={18} />
				</div>

				<div className="filters-section__controls">
					<Select
						value={filters.status}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'active', label: 'Active' },
							{ value: 'archived', label: 'Archived' },
						]}
					/>

					<Select
						value={filters.category}
						onChange={(e) => setCategoryFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Categories' },
							...categories.map((cat) => ({
								value: cat,
								label: cat.charAt(0).toUpperCase() + cat.slice(1),
							})),
						]}
					/>

					<div className="filters-section__sort-icon">
						<ArrowUpDown size={18} />
					</div>
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={features}
				actions={actions}
				loading={loading}
				emptyMessage="No features found. Create your first feature to get started."
				onRowClick={handleView}
			/>

			{/* Pagination */}
			{pagination.totalPages > 1 && (
				<div className="pagination">
					<div className="pagination__info">
						Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
					</div>
					<div className="pagination__controls">
						<Button
							variant="secondary"
							onClick={prevPage}
							disabled={pagination.page === 1 || loading}
							size="small"
						>
							Previous
						</Button>
						<Button
							variant="secondary"
							onClick={nextPage}
							disabled={pagination.page >= pagination.totalPages || loading}
							size="small"
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* Create Modal */}
			{showCreateModal && <FeatureForm mode="create" onClose={() => setShowCreateModal(false)} />}

			{/* Edit Modal */}
			{showEditModal && <FeatureForm mode="edit" onClose={() => setShowEditModal(false)} />}

			{/* View Modal */}
			{showViewModal && (
				<FeatureView
					featureData={selectedFeature}
					onClose={() => setShowViewModal(false)}
					onEdit={handleEditFromView}
				/>
			)}

			{/* Confirm Dialog (Delete/Restore) */}
			{showConfirmDialog && selectedFeatureForAction && (
				<ConfirmDialog
					isOpen={true}
					onConfirm={confirmAction}
					onCancel={cancelAction}
					title={confirmDetails.title}
					message={confirmDetails.message}
					confirmText={confirmDetails.confirmText}
					variant={confirmDetails.variant}
				/>
			)}
		</div>
	)
}

export default Features

